import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Kanban, Users, Building2, Calendar,
  BarChart3, Settings, LogOut, Moon, Sun, Menu, X,
  ChevronDown, Target, Bell, CreditCard
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

const navLinks = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Tableau de bord' },
  { path: '/pipeline', icon: Kanban, label: 'Pipeline' },
  { path: '/deals', icon: Target, label: 'Devis' },
  { path: '/contacts', icon: Users, label: 'Contacts' },
  { path: '/companies', icon: Building2, label: 'Entreprises' },
  { path: '/activities', icon: Calendar, label: 'Activités' },
  { path: '/paiements', icon: CreditCard, label: 'Paiements' },
  { path: '/reports', icon: BarChart3, label: 'Rapports' },
  { path: '/settings', icon: Settings, label: 'Paramètres' },
];

export default function Layout() {
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';

  const currentPage = navLinks.find(l =>
    location.pathname === l.path || location.pathname.startsWith(l.path + '/')
  );

  return (
    <TooltipProvider>
      <div className="flex h-screen bg-[#F4F5F7] dark:bg-[#0F1115]">
        {/* Sidebar */}
        <aside
          className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-white dark:bg-[#121420] border-r border-gray-200 dark:border-gray-800 flex flex-col fixed h-full z-20`}
          style={{ transition: 'width 250ms ease' }}
        >
          {/* Logo */}
          <div className="h-16 flex items-center px-4 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
            {sidebarOpen ? (
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-[#1B99E5] rounded-lg flex items-center justify-center shadow-sm">
                  <Kanban size={15} className="text-white" />
                </div>
                <span className="font-bold text-gray-900 dark:text-white text-base tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  PipelinePro
                </span>
              </div>
            ) : (
              <div className="w-8 h-8 bg-[#1B99E5] rounded-lg flex items-center justify-center mx-auto shadow-sm">
                <Kanban size={15} className="text-white" />
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-3 px-2 overflow-y-auto">
            {navLinks.map(({ path, icon: Icon, label }) => {
              const isActive = location.pathname === path || location.pathname.startsWith(path + '/');
              return (
                <Tooltip key={path} delayDuration={100}>
                  <TooltipTrigger asChild>
                    <Link
                      to={path}
                      data-testid={`nav-${path.replace('/', '')}`}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5 text-sm font-medium ${
                        isActive
                          ? 'bg-[#1B99E5]/10 text-[#1B99E5]'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                      }`}
                      style={{ transition: 'background-color 150ms ease, color 150ms ease' }}
                    >
                      <Icon size={18} className="flex-shrink-0" />
                      {sidebarOpen && <span>{label}</span>}
                    </Link>
                  </TooltipTrigger>
                  {!sidebarOpen && (
                    <TooltipContent side="right">
                      <p>{label}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              );
            })}
          </nav>

          {/* User section */}
          {sidebarOpen && (
            <div className="p-3 border-t border-gray-200 dark:border-gray-800 flex-shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2.5 w-full hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg p-2 text-left"
                    style={{ transition: 'background-color 150ms ease' }}
                    data-testid="user-menu-trigger"
                  >
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      <AvatarFallback className="bg-[#1B99E5]/20 text-[#1B99E5] text-xs font-bold">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{user?.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user?.role}</p>
                    </div>
                    <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={4} className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                    <Settings size={14} className="mr-2" /> Paramètres
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600" data-testid="logout-button">
                    <LogOut size={14} className="mr-2" /> Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </aside>

        {/* Main content */}
        <div
          className="flex-1 flex flex-col min-h-screen min-w-0"
          style={{ marginLeft: sidebarOpen ? '256px' : '64px', transition: 'margin-left 250ms ease' }}
        >
          {/* Header */}
          <header className="h-16 bg-white dark:bg-[#1A1D23] border-b border-gray-200 dark:border-gray-800 flex items-center justify-between px-4 sticky top-0 z-10 flex-shrink-0">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300"
                style={{ transition: 'background-color 150ms ease' }}
                data-testid="sidebar-toggle"
              >
                {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
              <div>
                <h1 className="text-sm font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {currentPage?.label || 'PipelinePro'}
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="h-9 w-9 p-0 rounded-lg"
                data-testid="theme-toggle"
              >
                {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg px-2 py-1.5"
                    style={{ transition: 'background-color 150ms ease' }}
                    data-testid="header-user-menu"
                  >
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="bg-[#1B99E5]/20 text-[#1B99E5] text-xs font-bold">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden sm:block">{user?.name?.split(' ')[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings size={14} className="mr-2" /> Paramètres
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
                    <LogOut size={14} className="mr-2" /> Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* Page content */}
          <main className="flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
